import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
  useToast
} from '@chakra-ui/react';
import {
  createPermission,
  updatePermission,
  deletePermission,
  getPermissionById
} from '../../../services/permissionService';

const PermissionForm = ({ permissionId, onClose }) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  
  const [permission, setPermission] = useState({
    name: '',
    method: '',
    endpoint: ''
  });

  const fetchPermission = async (id) => {
    try {
      const data = await getPermissionById(id);
      setPermission(data);
    } catch (error) {
      toast({
        title: 'Error fetching permission',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  useEffect(() => {
    if (permissionId) {
      fetchPermission(permissionId);
    }
  }, [permissionId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPermission((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const createMutation = useMutation(createPermission, {
    onSuccess: () => {
      queryClient.invalidateQueries('permissions');
      toast({
        title: 'Permission created.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error creating permission',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  });

  const updateMutation = useMutation(updatePermission, {
    onSuccess: () => {
      queryClient.invalidateQueries('permissions');
      toast({
        title: 'Permission updated.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error updating permission',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  });

  const deleteMutation = useMutation(deletePermission, {
    onSuccess: () => {
      queryClient.invalidateQueries('permissions');
      toast({
        title: 'Permission deleted.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error deleting permission',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (permissionId) {
      updateMutation.mutate({ id: permissionId, ...permission });
    } else {
      createMutation.mutate(permission);
    }
  };
  

  const handleDelete = () => {
    deleteMutation.mutate(permissionId);
  };

  if (permissionId && !permission.name && !permission.method && !permission.endpoint && !deleteMutation.isLoading) {
    return <Spinner />;
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <FormControl id="name" isRequired>
        <FormLabel>Name</FormLabel>
        <Input
          name="name"
          value={permission.name}
          onChange={handleChange}
          placeholder="Permission Name"
        />
      </FormControl>
      <FormControl id="method" isRequired mt={4}>
        <FormLabel>Method</FormLabel>
        <Select
          name="method"
          value={permission.method}
          onChange={handleChange}
          placeholder="Select Method"
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
        </Select>
      </FormControl>
      <FormControl id="endpoint" isRequired mt={4}>
        <FormLabel>Endpoint</FormLabel>
        <Input
          name="endpoint"
          value={permission.endpoint}
          onChange={handleChange}
          placeholder="Permission Endpoint"
        />
      </FormControl>
      <Button mt={4} colorScheme="teal" isLoading={createMutation.isLoading || updateMutation.isLoading} type="submit">
        {permissionId ? 'Update Permission' : 'Create Permission'}
      </Button>
      {permissionId && (
        <Button mt={4} ml={4} colorScheme="red" onClick={handleDelete} isLoading={deleteMutation.isLoading}>
          Delete Permission
        </Button>
      )}
    </Box>
  );
};

export default PermissionForm;
